import {
  type MachineAttachmentItemDto,
  type MachineQuotationItemDto,
  MachinesApiService,
  type SpecificationDto,
  type StockMachineDto
} from '@api/machines'
import { handleCatchError } from '@common/utils'
import { successToast } from '@components/reusable'
import { QUOTES_QUERIES_KEYS } from '@shared-queries/quotation'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { useLocation, useParams } from 'react-router-dom'
import { EQUIPMENT_TYPE } from '@/pages/customers/utils/constants'
import { storeActiveSpecName } from '@/pages/customers/utils/functions'

export const CUSTOMER_SPEC_KEYS = {
  ACTIVE_SPEC: 'activeSpec',
  EQUIPMENTS_BY_BRAND: 'equipmentsBybrand',
  SPEC_TEMPLATE: 'specTemplate',
  ARCHIVED_SPEC_EQUIPMENTS: 'archivedSpecEquipments'
}

export const useGetActiveSpec = () => {
  const { id: customerId, specId } = useParams()

  return useQuery({
    queryKey: [CUSTOMER_SPEC_KEYS.ACTIVE_SPEC, specId, customerId],
    queryFn: async () => {
      try {
        if (specId && specId !== 'undefined') {
          const response = await MachinesApiService.specSpec(
            String(specId),
            Number(customerId)
          )

          // save to session storage
          storeActiveSpecName(response.name)
          return response
        }
        return null
      } catch (error) {
        handleCatchError(error)
      }
    },
    throwOnError: false,
    staleTime: 0,
    refetchOnMount: 'always'
  })
}

export const useGetSpecTemplate = ({ templateId }: { templateId: string }) => {
  const location = useLocation()
  const queryParams = new URLSearchParams(location.search)
  const isEditingSpecItem = queryParams.get('edit')
  const { id, specId, equipmentId, attachmentId } = useParams()

  const isMachineItem =
    equipmentId && equipmentId !== 'undefined' && !attachmentId
  const isNestedAttachment =
    equipmentId &&
    equipmentId !== 'undefined' &&
    attachmentId &&
    attachmentId !== 'undefined'

  return useQuery({
    queryKey: [CUSTOMER_SPEC_KEYS.SPEC_TEMPLATE, specId, id, templateId],
    queryFn: async () => {
      try {
        const customerId = Number(id)
        const templateId = isMachineItem
          ? String(equipmentId)
          : String(attachmentId)

        if (!isNestedAttachment) {
          return await MachinesApiService.specTemplatestoEdit(
            String(specId),
            customerId,
            templateId
          )
        }
      } catch (error) {
        handleCatchError(error)
      }
    },
    throwOnError: false,
    staleTime: 30 * 60 * 1000,
    enabled: Boolean(isEditingSpecItem && !isNestedAttachment),
    refetchOnMount: 'always'
  })
}

export const useGetBrandEquipment = ({
  equipmentType,
  brandId
}: {
  equipmentType: string
  brandId: number
}) => {
  return useQuery({
    queryKey: [CUSTOMER_SPEC_KEYS.EQUIPMENTS_BY_BRAND, brandId],
    queryFn: async () => {
      try {
        let response: StockMachineDto[] = []

        switch (equipmentType) {
          case EQUIPMENT_TYPE.MACHINE: {
            response =
              await MachinesApiService.stocksGetMachinesByBrandId(brandId)
            break
          }
          case EQUIPMENT_TYPE.ATTACHMENT: {
            response =
              await MachinesApiService.stocksGetMachineAttachmentsByBrandId(
                brandId
              )
            break
          }
          default:
            break
        }
        return response
      } catch (error) {
        handleCatchError(error)
      }
    },
    throwOnError: false,
    staleTime: 30 * 60 * 1000
  })
}

export const useGetArchivedSpecEquipments = (customerId: number) => {
  return useMutation({
    mutationFn: async (specId: string) => {
      return await MachinesApiService.specsSpecsWithArchivedEquipment(
        specId,
        customerId
      )
    },
    throwOnError: false,
    onSuccess: () => {
      successToast()
    },
    onError: error => handleCatchError(error)
  })
}

export const useAddItemToSpec = (specId: string) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      equipmentType,
      equipmentId
    }: {
      equipmentType: string
      equipmentId: number
    }) => {
      let response: MachineQuotationItemDto | MachineAttachmentItemDto = {}

      switch (equipmentType) {
        case EQUIPMENT_TYPE.MACHINE: {
          response = await MachinesApiService.specMachineSpecInCache(
            specId,
            equipmentId
          )
          break
        }

        case EQUIPMENT_TYPE.ATTACHMENT: {
          response = await MachinesApiService.specMachineAttachmentSpecInCache(
            specId,
            equipmentId
          )
          break
        }

        default:
          break
      }

      return response
    },
    throwOnError: false,
    onSuccess: () => {
      successToast()

      queryClient.invalidateQueries({
        queryKey: [CUSTOMER_SPEC_KEYS.ACTIVE_SPEC, specId]
      })
    },
    onError: error => handleCatchError(error)
  })
}

export const useUpdateSpecName = ({
  specId,
  customerId
}: {
  specId: string
  customerId: number
}) => {
  const { t } = useTranslation('', { keyPrefix: 'specs.grid' })
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (specName: string) => {
      return await MachinesApiService.customersUpdateSpecName(
        customerId,
        specId,
        specName
      )
    },
    throwOnError: false,
    onSuccess: response => {
      successToast(t('specNameUpdated'))

      const updatedItemId = response.id

      queryClient.invalidateQueries({
        queryKey: [CUSTOMER_SPEC_KEYS.ACTIVE_SPEC, specId]
      })

      queryClient.setQueryData<SpecificationDto[]>(
        [QUOTES_QUERIES_KEYS.QUOTE_SPECS_BY_CUSTOMER, customerId],
        (oldData?: SpecificationDto[]) => {
          if (!oldData) {
            return
          }

          const cachedData = [...oldData]
          const cachedElementToReplace = cachedData.findIndex(
            (item: SpecificationDto) => item.id === updatedItemId
          )

          cachedData[cachedElementToReplace] = response

          return cachedData
        }
      )
    },
    onError: error => handleCatchError(error)
  })
}

export const useUpdateCustomerSpecNote = (specId: string) => {
  const { t } = useTranslation('', { keyPrefix: 'specs' })
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      customerId,
      note
    }: {
      customerId: number
      note: string
    }) => {
      return await MachinesApiService.customersUpdateSpecNote(
        customerId,
        specId,
        note
      )
    },
    throwOnError: false,
    onSuccess: () => {
      successToast(t('noteSaved'))

      queryClient.invalidateQueries({
        queryKey: [CUSTOMER_SPEC_KEYS.ACTIVE_SPEC, specId]
      })
    },
    onError: error => handleCatchError(error)
  })
}

export const useCreateSpec = (customerId: number) => {
  const { t } = useTranslation('', { keyPrefix: 'specs.specDialog' })
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (specName: string) => {
      const response = await MachinesApiService.specAddSpec(
        customerId,
        specName
      )

      // save to session storage
      storeActiveSpecName(response.name)

      return response
    },
    throwOnError: false,
    onSuccess: () => {
      successToast(t('newSpecCreated'))

      queryClient.invalidateQueries({
        queryKey: [QUOTES_QUERIES_KEYS.QUOTE_SPECS_BY_CUSTOMER, customerId]
      })
    },
    onError: error => handleCatchError(error)
  })
}

export const useDeleteCustomerSpec = (customerId: number) => {
  const { t } = useTranslation('', { keyPrefix: 'specs.grid.actions.delete' })
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (specId: string) => {
      return await MachinesApiService.specRemoveSpecById(specId, customerId)
    },
    throwOnError: false,
    onSuccess: () => {
      successToast(t('specDeleted'))

      queryClient.invalidateQueries({
        queryKey: [QUOTES_QUERIES_KEYS.QUOTE_SPECS_BY_CUSTOMER, customerId]
      })
    },
    onError: error => handleCatchError(error)
  })
}

export const useDeleteSpecTemplate = (specId: string) => {
  const { t } = useTranslation('', { keyPrefix: 'specs' })
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      customerId,
      templateId
    }: {
      customerId: number
      templateId: string
    }) => {
      const response = await MachinesApiService.customersDeleteSpecTemplate(
        specId,
        customerId,
        templateId
      )
      storeActiveSpecName(response.name)
      return response
    },
    throwOnError: false,
    onSuccess: () => {
      successToast(t('specTemplateDeleted'))

      queryClient.invalidateQueries({
        queryKey: [CUSTOMER_SPEC_KEYS.ACTIVE_SPEC, specId]
      })
    },
    onError: error => handleCatchError(error)
  })
}

export const useCloneCustomerSpec = (customerId: number) => {
  const { t } = useTranslation('', {
    keyPrefix: 'specs.grid.actions.duplicate'
  })
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (specId: string) => {
      return await MachinesApiService.specClone(customerId, specId)
    },
    throwOnError: false,
    onSuccess: () => {
      successToast(t('specDuplicated'))

      queryClient.invalidateQueries({
        queryKey: [QUOTES_QUERIES_KEYS.QUOTE_SPECS_BY_CUSTOMER, customerId]
      })
    },
    onError: error => handleCatchError(error)
  })
}

export const useCopySpecToCustomer = () => {
  return useMutation({
    mutationFn: async ({
      customerId,
      specId,
      targetCustomerId
    }: {
      customerId: number
      specId: string
      targetCustomerId: number
    }) => {
      return await MachinesApiService.specCopyToCustomer(
        customerId,
        specId,
        targetCustomerId
      )
    },
    throwOnError: false,
    onSuccess: () => {
      successToast()
    },
    onError: error => handleCatchError(error)
  })
}

export const useCopyEquipments = () => {
  const { t } = useTranslation('', { keyPrefix: 'specs.specDialog' })
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      customerId,
      sourceSpecId,
      targetSpecId,
      sourceTemplateId,
      items
    }: {
      customerId: number
      sourceSpecId: string
      targetSpecId: string
      sourceTemplateId: string
      items: number[]
    }) => {
      return (await MachinesApiService.specCopyToItem(
        customerId,
        sourceSpecId,
        targetSpecId,
        sourceTemplateId,
        items
      )) as SpecificationDto
    },
    throwOnError: false,
    onSuccess: (response, { targetSpecId, sourceSpecId }) => {
      const isNewSpec = targetSpecId !== sourceSpecId
      const toastMessage = t('specSuccesfullyCopied', {
        specName: response.name
      })

      successToast(isNewSpec ? toastMessage : 'Success')

      queryClient.invalidateQueries({
        queryKey: [
          CUSTOMER_SPEC_KEYS.ACTIVE_SPEC,
          isNewSpec ? targetSpecId : sourceSpecId
        ]
      })
    },
    onError: error => handleCatchError(error)
  })
}
